import type Konva from "konva";
import { useCallback, useState } from "react";

import { type InteractionMode } from "@design-lab/widgets/artboard/model/state";
import { extractPanFromPosition } from "../lib/refinePositioning";
import { useRefineButtonSync } from "./useRefineButtonSync";
import { useRefineGestures } from "./useRefineGestures";
import { usePlacementZoomAndPan } from "./usePlacementZoomAndPan";

type UseRefinePanAndZoomProps = {
  dimensions: { width: number; height: number };
  zoomCenter?: { x: number; y: number } | undefined;
  stageRef: React.RefObject<Konva.Stage | null>;
  contentBounds?:
    | {
        x: number;
        y: number;
        width: number;
        height: number;
      }
    | undefined;
  interactionMode: InteractionMode;
  isSpaceHeld: boolean;
  minZoom?: number;
  maxZoom?: number;
};

export function useRefinePanAndZoom({
  dimensions,
  zoomCenter,
  stageRef,
  contentBounds,
  interactionMode,
  isSpaceHeld,
  minZoom = 1,
  maxZoom = 10
}: UseRefinePanAndZoomProps) {
  const { setZoom, setPan } = usePlacementZoomAndPan();

  const [isGestureActive, setIsGestureActive] = useState(false);

  const handleGestureStart = useCallback(() => {
    setIsGestureActive(true);
  }, []);

  const handleGestureEnd = useCallback(
    (finalZoom: number, finalPosition: { x: number; y: number }) => {
      // Extract pan offset from the final stage position
      const pan = extractPanFromPosition({
        stagePosition: finalPosition,
        zoom: finalZoom,
        dimensions,
        zoomCenter
      });

      // Sync final values back to atoms for UI display and button behavior
      setZoom(finalZoom);
      setPan(pan);
      setIsGestureActive(false);
    },
    [setZoom, setPan, zoomCenter, dimensions]
  );

  // Hook for handling all gestures (mouse wheel, pinch, drag pan)
  const gestureHandlers = useRefineGestures({
    dimensions,
    zoomCenter,
    stageRef,
    contentBounds,
    interactionMode,
    isSpaceHeld,
    minZoom,
    maxZoom,
    onGestureStart: handleGestureStart,
    onGestureEnd: handleGestureEnd
  });

  // Hook for syncing button clicks to stage
  useRefineButtonSync({
    stageRef,
    dimensions,
    zoomCenter,
    isGestureActive
  });

  return gestureHandlers;
}
